/* eslint-disable max-lines */
import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMe, getMeUid } from '@api/selectors/auth';
import useOfflineCentreStore from '@stores/city-specific-offline-centres';
import styled from '@emotion/styled';
import SearchableDropdown from '@comp/Dropdown/SearchableDropdown';
import Button from '@base/Button/Button';
import { segment } from '@actions/analytics';

// import Drawer from '@base/Drawer/Drawer';
import Drawer from '@base/Drawer/DrawerWithFooter';
import Typography from 'aquilla/core/atoms/Typography';
import NextImage from '@base/FutureImage';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import { capitalize } from '@utils/textHelper';
import {
  bookASlotForCentreVisit,
  fetchOfflineCityCentresOfAGoalV2,
  getCentreDetails
} from '@api/fetchers/citySpecificOfflineCentre';
import { simpleFetch as fetcher } from '@api/fetchers';
import useCentreInfo from '@api/hooks/citySpecificOfflineCentres/useCentreInfo';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import useWindowSize from '@hooks/useWindowSize';

// import { getIsLoggedIn, getMeUid } from '@api/selectors/auth';

import {
  getDDMMYYYYFormat,
  getDateMonthString,
  getWeekDayString
} from '@utils/dates';
import useOfflineCentreEnabledGoals from '@api/hooks/citySpecificOfflineCentres/useOfflineCentreEnabledGoalsFromBE';
import Toast from 'aquilla/core/molecules/Toast';
import boxShadowGenerator from '@utils/boxShadowGenerator';

import { OFFLINE_GOAL_UID } from '@cont/OfflineCentres/constants';
import { CENTRE_GOAL_PAGE, FREE_HOME_PAGE } from '@constants/segment';

import { getCMSHost } from '@utils/api-helper/host';
import {
  OFFLINE_VIST_GOAL_SELECTED,
  OFFLINE_VIST_CENTRE_SELECTED,
  OFFLINE_VIST_SLOT_SELECTED,
  OFFLINE_VIST_BOOK_CLICKED,
  OFFLINE_VIST_BOOK_SLOTS_SHOWN,
  OFFLINE_VIST_BOOK_SUCCESSFUL,
  OFFLINE_VIST_BOOK_CANCEL_CLICKED
} from './constants';

const Container = styled.div`
  & .toast {
    z-index: 4000;
  }
`;

const DrawerWrapper = styled.div`
  grid-column: span 10;
  &.MuiPopover-root .MuiPaper-root {
    z-index: 2000;
  }
`;

const UserInfoDiv = styled.div`
  display: flex;
  margin-bottom: ${Spacings.SPACING_6B};
`;
const PhoneEmail = styled(Typography)`
  margin-top: ${Spacings.SPACING_1B};
  color: var(--color-text-secondary);
`;

const InfoDiv = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: ${Spacings.SPACING_4B};
`;

const StyledBook = styled(Typography)`
  margin-top: ${Spacings.SPACING_1B};
  margin-bottom: ${Spacings.SPACING_6B};
`;

const StyledDropdown = styled(SearchableDropdown)`
  margin-bottom: ${Spacings.SPACING_4B};
  align-items: center;
  gap: ${Spacings.SPACING_2B};
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_1B};
  height: ${Spacings.SPACING_12B};
  display: flex;
  background-color: var(--color-base-1);
  justify-content: space-around;
  :hover {
    border: 1px solid;
  }
  .search-dd-input-wrapper {
    width: 90%;
  }
  .search-dd-options {
    margin-top: ${Spacings.SPACING_3B};
    max-height: 276px;
    padding: ${Spacings.SPACING_0B};
    top: 42px;
    border: 1px solid var(--color-divider);
    .no-dd-option {
      padding: 14px 16px;
    }
  }
`;
const StyDrawer = styled(Drawer)`
  &.MuiDrawer-root > .MuiPaper-root {
    background-color: var(--color-base-0);
  }
`;
const SlotsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: ${Spacings.SPACING_4B};
`;

const SlotDiv = styled.div`
  display: flex;
  width: 143px;
  /* height: 32px; */
  border: ${({ $selected }) =>
    $selected
      ? '1px solid var(--color-base-fill)'
      : '1px solid var(--color-divider)'};
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_6B};
  flex-shrink: 0;
  margin-right: ${({ $isThird }) => ($isThird ? '0px;' : Spacings.SPACING_4B)};
  margin-bottom: ${Spacings.SPACING_4B};
  border-radius: ${Spacings.SPACING_1B};
  background-color: var(--color-base-1);
  cursor: pointer;
  justify-content: center;
  ${mediaQuery.uptoMobile} {
    margin-right: ${({ $isThird }) =>
      $isThird ? '0px;' : Spacings.SPACING_2B};
    padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_2B};
    width: 31%;
  }
`;

const SlotText = styled(Typography)`
  font-size: ${Fonts.H7};
  line-height: ${Fonts.HEADER_LINE_HEIGHT};
`;

const StyTypography = styled(Typography)`
  font-weight: ${Fonts.SEMIBOLD};
  margin-left: ${Spacings.SPACING_1B};
  color: var(--color-text-secondary);
`;

const tempChangeArray = (list) => {
  return list.map((item) => {
    return { ...item, label: item.goalName };
  });
};

const FooterWrapper = styled.div`
  width: 100%;
  background-color: var(--color-base-1);
  display: flex;
  align-items: center;
  /* justify-content: center; */
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_10B};
  justify-content: space-between;
  box-shadow: ${boxShadowGenerator({ yOffset: `-${Spacings.SPACING_4B}` })};
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    height: unset;
    padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_4B};
    background-color: unset;
    box-shadow: unset;
  }
`;

const StyButton = styled(Button)`
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_4B};
  }
`;
const FooterLeftDiv = styled.div`
  display: flex;
`;

const DropdownItem = styled.div`
  height: ${Spacings.SPACING_12B};
  align-items: center;
  padding: ${Spacings.SPACING_14} ${Spacings.SPACING_4B};
  :hover {
    background-color: var(--color-divider);
  }
`;

const Footer = ({ handleButtonClick, showFooterButton }) => {
  const { isMobile } = useWindowSize();

  return (
    <FooterWrapper>
      <FooterLeftDiv>
        <NextImage
          src="offline-centres/notification.svg"
          alt="notification"
          width="16"
          height="16"
        />
        <StyTypography variant="p3">
          Centres will be open from 10 a.m. - 7 p.m.
        </StyTypography>
      </FooterLeftDiv>

      {showFooterButton ? (
        <StyButton
          label="Book my visit"
          color="black"
          onClick={handleButtonClick}
          grid={2}
          width={isMobile ? '100%' : '160px'}
        />
      ) : (
        <StyButton
          label="Book my visit"
          color="black"
          onClick={handleButtonClick}
          grid={2}
          width={isMobile ? '100%' : '160px'}
          variant="disabled"
        />
      )}
    </FooterWrapper>
  );
};

const BookAVisit = ({ newGoalPage, currentCentreDetails }) => {
  const router = useRouter();
  const { query } = router;
  let goalUID = '';
  let centreUID = '';
  const CMSHost = getCMSHost();
  if (query?.goalUID) {
    goalUID = query?.goalUID;
  } else {
    goalUID = currentCentreDetails?.goalUID;
  }
  if (query?.centreUID) {
    centreUID = query?.centreUID;
  } else {
    centreUID = currentCentreDetails?.centreUID;
  }

  const userId = useMyInfo(getMeUid);
  const { isMobile } = useWindowSize();

  const {
    isBookAVisitFlow,
    setBookAVisitFlow,
    setIsVisitBooked,
    setVisitBookedData,
    setBookCentreList,
    selectedCentreBEData
  } = useOfflineCentreStore();

  // Fetching offline enabled goals
  const { offlineCentresGoalList = {} } = useOfflineCentreEnabledGoals();
  const { activeGoals = [] } = offlineCentresGoalList || {};

  let selectedOfflineGoal = null;
  let changegGoalList = [];

  if (activeGoals) {
    changegGoalList = tempChangeArray(activeGoals);
    selectedOfflineGoal = [...changegGoalList]?.find(
      (goal) => goal.uid === goalUID
    );
  }
  const [selectedGoal, setSelectedGoal] = useState(selectedOfflineGoal);
  const [pageLanded, setPageLanded] = useState(false);
  const [centreDetails, setCentreDetails] = useState({});
  const [skuid, setSkuId] = useState();

  // api will take time so need of use effect to pre fill dropdown
  useEffect(() => {
    if (selectedOfflineGoal?.uid) {
      if (!pageLanded && goalUID === OFFLINE_GOAL_UID) {
        setSelectedGoal({});
        return;
      }
      setSelectedGoal(selectedOfflineGoal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOfflineGoal?.uid]);

  const fetchSkuID = useCallback(async () => {
    const respData = await fetcher(`${CMSHost}centre-sku/`);
    if (respData.skuId) {
      const skuIdTrimmed = respData.skuId.trim();
      if (skuIdTrimmed) {
        setSkuId(skuIdTrimmed);
      }
    }
  }, [CMSHost]);

  useEffect(() => {
    fetchSkuID();
  }, [fetchSkuID]);

  const [centresList, setCentresList] = useState([]);
  const [centresFetchedFirstTime, setcentresFetchedFirstTime] = useState(0);

  // this will fire on change of goal
  useEffect(() => {
    if (selectedGoal?.uid) {
      const body = {
        goal_uid: selectedGoal?.uid,
        user_uid: userId
      };
      fetchOfflineCityCentresOfAGoalV2({ body }).then((res) => {
        const { centres: centresListRes } = res;
        setCentresList(centresListRes);
        setBookCentreList(centresListRes);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGoal]);

  const selectedCentre = centresList?.find(
    (centre) => centre.uid === centreUID
  );
  const [bookACentreOption, setBookACentreOption] = useState(
    selectedCentre || null
  );

  const [slotClicked, setSlotClicked] = useState(0);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    if (selectedCentre && !centresFetchedFirstTime) {
      if (goalUID !== OFFLINE_GOAL_UID) setBookACentreOption(selectedCentre);
      setcentresFetchedFirstTime(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCentre, centresFetchedFirstTime]);

  useEffect(() => {
    if (shouldRedirect && skuid) {
      router.push(`https://unacademy.com/goal/-/-/subscribe/${skuid}`);
    }
  }, [shouldRedirect, router, skuid]);

  const meData = useMyInfo(getMe);
  const { firstName, lastName, email, phone } = meData;

  const { centreInfo } = useCentreInfo(goalUID, centreUID);

  const { centreName: cmsCentreName, cityName: cmsCityName } = centreInfo || {};
  const { goalInfo: goalData = {} } = useGoalInfo(goalUID);

  const { city, state, name: centreName } = selectedCentreBEData || {};

  const onGoalChange = (option) => {
    segment.track(OFFLINE_VIST_GOAL_SELECTED, {
      city: city || cmsCityName,
      state,
      centre_name: centreName || cmsCentreName,
      goal_name: goalData?.name,
      goal_uid: goalUID,
      selected_goal: option.name,
      last_primary_source: newGoalPage ? FREE_HOME_PAGE : CENTRE_GOAL_PAGE
    });
    setBookACentreOption(null);
    setSlotClicked(-1);
    setPageLanded(true);

    setSelectedGoal(option);
  };
  const datesArry = [];
  let formattedData = [];
  for (let day = 0; day <= 8; day += 1) {
    const currDate = new Date(new Date().setUTCHours(0, 0, 0, 0));
    datesArry.push(currDate.setDate(currDate.getDate() + day));
  }

  formattedData = datesArry.map(
    (date) =>
      `${getDateMonthString(date, false, false)} • ${getWeekDayString(date)}`
  );
  const renderSlots = () => {
    const handleSlotClick = (index) => () => {
      setSlotClicked(index);
      segment.track(OFFLINE_VIST_SLOT_SELECTED, {
        city: city || cmsCityName,
        state,
        centre_name: centreName || cmsCentreName,
        goal_name: goalData?.name,
        goal_uid: goalUID,
        selected_slot: formattedData[slotClicked],
        last_primary_source: newGoalPage ? FREE_HOME_PAGE : CENTRE_GOAL_PAGE
      });
    };

    const slots = formattedData.map((date, index) => {
      return (
        <SlotDiv
          key={date}
          $isThird={(index + 1) % 3 === 0}
          onClick={handleSlotClick(index)}
          $selected={index === slotClicked}
        >
          <SlotText variant="h6">{date}</SlotText>
        </SlotDiv>
      );
    });
    return slots;
  };

  const [showToast, setShowToast] = useState(false);
  const closeToast = () => setShowToast(false);

  // api call for booking slot
  const handleBookSlot = () => {
    segment.track(OFFLINE_VIST_BOOK_CLICKED, {
      city: city || cmsCityName,
      state,
      centre_name: centreName || cmsCentreName,
      goal_name: goalData?.name,
      goal_uid: goalUID,
      selected_goal: selectedGoal?.name,
      selected_centre: bookACentreOption?.name,
      selected_slot: formattedData[slotClicked],
      last_primary_source: newGoalPage ? FREE_HOME_PAGE : CENTRE_GOAL_PAGE
    });
    const body = {
      centre_uid: bookACentreOption?.uid,
      goal_uid: selectedGoal?.uid,
      visit_time: datesArry[slotClicked] / 1000
    };
    bookASlotForCentreVisit({ body }).then((res) => {
      if (res.error) {
        setShowToast(true);
      } else {
        segment.track(OFFLINE_VIST_BOOK_SUCCESSFUL, {
          city: city || cmsCityName,
          state,
          centre_name: centreName || cmsCentreName,
          goal_name: goalData?.name,
          goal_uid: goalUID,
          selected_goal: selectedGoal?.name,
          selected_centre: bookACentreOption?.name,
          selected_slot: formattedData[slotClicked],
          last_primary_source: newGoalPage ? FREE_HOME_PAGE : CENTRE_GOAL_PAGE,
          centre_address: centreDetails?.address || 'NA',
          gmap_link:
            centreDetails?.gmapLink ||
            `http://maps.google.com/maps?q=${centreDetails?.lat},${centreDetails?.long}` ||
            'NA',
          visit_slot: `${getDDMMYYYYFormat(
            new Date(datesArry[slotClicked])
          )} 00:00:00`
        });
        setIsVisitBooked(true);
        setBookAVisitFlow(false);
        setVisitBookedData({
          centreUID: bookACentreOption.uid,
          goalUID: selectedGoal.uid,
          visitTime: datesArry[slotClicked]
        });
        setShouldRedirect(true);
      }
    });
  };
  const showFooterButton =
    selectedGoal?.uid && bookACentreOption?.uid && slotClicked !== -1;

  const onCentreClick = (option) => {
    setBookACentreOption(option);
    segment.track(OFFLINE_VIST_CENTRE_SELECTED, {
      city: city || cmsCityName,
      state,
      centre_name: centreName || cmsCentreName,
      goal_name: goalData?.name,
      goal_uid: goalUID,
      selected_centre: option?.name,
      last_primary_source: newGoalPage ? FREE_HOME_PAGE : CENTRE_GOAL_PAGE
    });
  };
  useEffect(() => {
    segment.track(OFFLINE_VIST_BOOK_SLOTS_SHOWN, {
      city: city || cmsCityName,
      state,
      centre_name: centreName || cmsCentreName,
      goal_name: goalData?.name,
      goal_uid: goalUID,
      last_primary_source: newGoalPage ? FREE_HOME_PAGE : CENTRE_GOAL_PAGE
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (bookACentreOption?.uid)
      getCentreDetails(bookACentreOption?.uid).then((res) => {
        setCentreDetails(res);
      });
  }, [bookACentreOption, selectedGoal]);

  const closeBookAVisitFlow = () => {
    setBookAVisitFlow(false);
    segment.track(OFFLINE_VIST_BOOK_CANCEL_CLICKED, {
      city: city || cmsCityName,
      state,
      centre_name: centreName || cmsCentreName,
      goal_name: goalData?.name,
      goal_uid: goalUID,
      selected_goal: selectedGoal?.name,
      last_primary_source: newGoalPage ? FREE_HOME_PAGE : CENTRE_GOAL_PAGE
    });
  };

  const renderGoalList = (item) => {
    return (
      <DropdownItem onClick={() => setSelectedGoal(item)}>
        {item?.name}
      </DropdownItem>
    );
  };

  const renderCentreList = (item) => {
    return (
      <DropdownItem onClick={() => setBookACentreOption(item)}>
        {item?.name}
      </DropdownItem>
    );
  };

  return (
    <Container>
      <Toast
        close={closeToast}
        open={showToast}
        type="error"
        // icon={<Copy color="var(--color-base-1)" />}
      >
        Something went wrong
      </Toast>

      <StyDrawer
        drawerState={isBookAVisitFlow}
        onClose={closeBookAVisitFlow}
        footer={
          <Footer
            handleButtonClick={handleBookSlot}
            showFooterButton={showFooterButton}
          />
        }
      >
        <DrawerWrapper>
          <Typography variant="h4">Book your centre visit</Typography>
          <StyledBook variant="p2">
            Select your preferred centre and slot
          </StyledBook>
          <UserInfoDiv>
            <NextImage
              src="offline-centres/bookAvatar.svg"
              alt="avatar"
              width={40}
              height={40}
            />
            <InfoDiv>
              <Typography variant="h5">
                {capitalize(firstName)} {lastName}
              </Typography>
              <PhoneEmail variant="p2">
                {phone} • {email}
              </PhoneEmail>
            </InfoDiv>
          </UserInfoDiv>

          <StyledDropdown
            options={changegGoalList}
            selectedOption={selectedGoal || {}}
            menuWidth={isMobile ? '100%' : '464px'}
            onOptionClick={onGoalChange}
            buttonWidth={isMobile ? '100%' : '464px'}
            placeholder="Search your goal"
            dropdownItem={renderGoalList}
            labelKey="name"
            className="goal-dropdown"
            clearOnSelect
          />
          <StyledDropdown
            options={centresList}
            isDisabled={!centresList || centresList.length === 0}
            selectedOption={bookACentreOption || {}}
            placeholder="Search your centre"
            menuWidth={isMobile ? '100%' : '464px'}
            onOptionClick={onCentreClick}
            buttonWidth={isMobile ? '100%' : '464px'}
            dropdownItem={renderCentreList}
            labelKey="name"
            className="centre-dropdown"
            clearOnSelect
          />

          <SlotsWrapper>{renderSlots()}</SlotsWrapper>
        </DrawerWrapper>
      </StyDrawer>
    </Container>
  );
};

export default BookAVisit;
